<template>
    <!-- overflow-x-clip, not overflow-hidden: a hidden box would become the scroll container of
         every position: sticky element below it, and those would never stick to the page. -->
    <div class="flex h-full flex-col overflow-x-clip">
        <Toast class="app-toast mt-8" position="top-center" @close="onToastClosed" @life-end="onToastClosed">
            <template #message="{ message }">
                <article
                    :class="toastCardClass(message)"
                    :role="toastAriaRole(message)"
                    :aria-live="toastAriaLive(message)"
                    aria-atomic="true"
                >
                    <div class="app-toast__indicator" aria-hidden="true"></div>
                    <div class="app-toast__copy">
                        <p class="app-toast__summary">{{ message.summary || 'Notification' }}</p>
                        <p v-if="message.detail" class="app-toast__detail">{{ message.detail }}</p>
                    </div>
                    <div
                        v-if="message.life"
                        class="app-toast__progress"
                        :style="toastProgressStyle(message)"
                        aria-hidden="true"
                    >
                    </div>
                </article>
            </template>
        </Toast>

        <div id="root-fullscreen" />
        <!-- PAGE HEADER -->
        <!-- The content will be automatically injected here by components/Header.vue -->
        <div id="header" ref="headerRef" class="header-sticky" :class="{ 'min-h-8': $isMobile }" />

        <ProgressBar v-show="isLoading" mode="indeterminate" style="height: 6px"></ProgressBar>

        <!-- PAGE CONTENT -->
        <div
            id="topWindow"
            ref="mainContentRef"
            :style="{ transform: `translateX(${translateX}px)` }"
            fullscreen
            class="min-h-[var(--app-viewport-height)]"
            :class="{
                'flex items-center justify-center': !hasTabs || !$store.state.auth.initialCheckDone,
                'opacity-10': Math.abs(translateX) > pixelsSwipeThreshold,
                'opacity-70': Math.abs(translateX) > 0 && Math.abs(translateX) < pixelsSwipeThreshold,
            }"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
        >
            <main class="w-full" :style="mainContentStyle">
                <div v-if="!$store.state.auth.initialCheckDone" class="flex h-full w-full items-center justify-center">
                    <Loader :size="100" />
                </div>
                <div v-else class="h-full w-full">
                    <div v-if="$isMobile && hasTabs" class="min-h-0 w-full flex-1 p-4">
                        <router-view name="mobile" />
                        <div class="h-2 w-full shrink-0" aria-hidden="true"></div>
                    </div>

                    <Navbar v-else-if="hasTabs">
                        <div class="h-full w-full p-4">
                            <router-view />
                        </div>
                    </Navbar>

                    <div v-else class="h-full w-full p-4">
                        <router-view />
                    </div>

                    <!-- !!! here it is mandatory to only load the modal when logged in because the may cause an infinite loop -->
                    <ModalsGroup v-if="$store.getters['auth/loggedIn']" />
                </div>
            </main>
        </div>
        <!-- PAGE FOOTER (TABS) FOR MOBILE APP -->
        <div
            v-if="$isMobile && hasTabs && $store.state.auth.initialCheckDone"
            ref="tabsRef"
            class="footer-sticky mobile-liquid-tabs"
            @click="onTabBarTap"
        >
            <div
                ref="tabsBarRef"
                class="mobile-liquid-tabs__bar grid items-center justify-items-center"
                :class="{
                    'is-dragging': isTabLensDragging,
                    'is-pressed': isTabLensPressed,
                    'is-ready': isTabLensReady,
                }"
                :style="tabLensStyle"
                @click.capture="handleTabsClickCapture"
                @pointerdown="handleTabsPointerDown"
                @pointermove="handleTabsPointerMove"
                @pointerup="handleTabsPointerUp"
                @pointercancel="handleTabsPointerCancel"
                @lostpointercapture="handleTabsPointerCancel"
            >
                <div v-show="mobileVisualActiveTabIndex >= 0" class="mobile-liquid-tabs__lens" aria-hidden="true">
                    <div class="mobile-liquid-tabs__lens-shine"></div>
                </div>
                <router-link
                    tag="div"
                    :to="{ name: 'sessions.index' }"
                    data-tab-index="0"
                    class="mobile-liquid-tabs__link flex h-full w-full items-center justify-center"
                >
                    <div
                        class="mobile-liquid-tabs__item flex flex-col items-center justify-center text-center text-xs font-light"
                        :class="{ 'is-active': mobileVisualActiveTabIndex === 0 }"
                    >
                        <ListBulletIcon class="h-6 w-6" />
                        <div class="mobile-liquid-tabs__label">{{ $t('session.title', 2) }}</div>
                    </div>
                </router-link>

                <router-link
                    tag="div"
                    :to="{ name: 'horses.index' }"
                    data-tab-index="1"
                    class="mobile-liquid-tabs__link flex h-full w-full items-center justify-center"
                >
                    <div
                        class="mobile-liquid-tabs__item flex flex-col items-center justify-center text-center text-xs font-light"
                        :class="{ 'is-active': mobileVisualActiveTabIndex === 1 }"
                    >
                        <DressageIcon class="w-8" />
                        <div class="mobile-liquid-tabs__label">{{ $t('horse.title', 2) }}</div>
                    </div>
                </router-link>

                <router-link
                    tag="div"
                    :to="{
                        name:
                            $movepro.current.isRecording || $movepro.current.isPaused
                                ? 'record.live'
                                : $store.state.phoneRecording?.isRecording
                                  ? 'record.simple'
                                  : 'record.index',
                    }"
                    data-tab-index="2"
                    class="mobile-liquid-tabs__link flex h-full w-full items-center justify-center"
                >
                    <div
                        class="mobile-liquid-tabs__item flex flex-col items-center justify-center text-center text-xs font-light"
                        :class="{ 'is-active': mobileVisualActiveTabIndex === 2 }"
                    >
                        <PlayCircleIcon
                            v-if="
                                $movepro.current.isRecording ||
                                $movepro.current.isPaused ||
                                $store.state.phoneRecording?.isRecording
                            "
                            class="text-state-green h-6 w-6 animate-pulse"
                        />
                        <CloudArrowDownIcon
                            v-else-if="$movepro.current.isMemoryMode"
                            class="text-state-yellow h-6 w-6 animate-pulse"
                        />
                        <RecordIcon v-else class="h-6 w-6" />
                        <div class="mobile-liquid-tabs__label">{{ $t('record.title') }} </div>
                    </div>
                </router-link>
                <router-link
                    tag="div"
                    :to="{ name: 'stablehealth.index' }"
                    data-tab-index="3"
                    class="mobile-liquid-tabs__link flex h-full w-full items-center justify-center"
                >
                    <div
                        class="mobile-liquid-tabs__item flex flex-col items-center justify-center text-center text-xs font-light"
                        :class="{ 'is-active': mobileVisualActiveTabIndex === 3 }"
                    >
                        <HeartPulseIcon class="h-6 w-6" />
                        <div class="mobile-liquid-tabs__label">{{ $t('stablehealth.menu') }}</div>
                    </div>
                </router-link>
                <!-- <router-link
                        v-else
                        tag="div"
                        :to="{ name: 'teams.sessions.index' }"
                        class="h-full grow flex items-center justify-center"
                    >
                        <div
                            class="flex flex-col justify-center items-center text-xs space-y-1 font-light"
                            :class="{ 'text-brand-base': currentRouteLayout === 'teams' }"
                        >
                            <UsersIcon class="w-6 h-6" />
                            <div>{{ $t('team.title', 2) }}</div>
                        </div>
                    </router-link> -->
                <router-link
                    tag="div"
                    :to="{ name: 'settings.index' }"
                    data-tab-index="4"
                    class="mobile-liquid-tabs__link flex h-full w-full items-center justify-center"
                >
                    <div
                        class="mobile-liquid-tabs__item flex flex-col items-center justify-center text-center text-xs font-light"
                        :class="{ 'is-active': mobileVisualActiveTabIndex === 4 }"
                    >
                        <div class="relative">
                            <SettingsIcon class="h-6 w-6" />
                            <Badge
                                v-if="pendingInvitations > 0"
                                :text="pendingInvitations"
                                :color="tailwind.theme.color.brand.red"
                                class="absolute -top-2 -right-3 h-4"
                            />
                        </div>
                        <div class="mobile-liquid-tabs__label">{{ $t('menu.setting', 2) }}</div>
                    </div>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, provide, onMounted, onUnmounted, watch, inject, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { Network } from '@capacitor/network'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'
import axios from '@/lib/API/client'

import tailwind from '@/lib/tailwind'
import Memory from '@/lib/memory'
import {
    CALLBACK_QUERY_FEEDBACK,
    callbackRequiresAuth,
    getCallbackName,
    normalizeQueryValue,
    processCallbackQuery,
    readRedirectTarget,
} from '@/lib/helpers/deeplink'
import { loadTranslations } from '@/i18n'
import useAppUpdate from '@/lib/hooks/use-app-update'
import useBanners from '@/lib/hooks/use-banners'
import useMetas from '@/lib/hooks/use-metas'
import { badgeCount } from '@/lib/invitations'
import { isTemporarySubscription } from '@/lib/premium-access'

import ProgressBar from 'primevue/progressbar'

import { ListBulletIcon, UsersIcon, PlayCircleIcon, CloudArrowDownIcon } from '@heroicons/vue/24/solid'
import DressageIcon from '@/components/icons/DressageIcon.vue'
import HeartPulseIcon from '@/components/icons/HeartPulseIcon.vue'
import SettingsIcon from '@/components/icons/SettingsIcon.vue'
import Badge from '@/components/Badge.vue'
import RecordIcon from '@/components/icons/RecordIcon.vue'
import Loader from '@/components/Loader.vue'
import ModalsGroup from '@/components/modals/ModalsGroup.vue'
import Navbar from '@/layouts/Navbar.vue'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const route = useRoute()
const store = useStore()
// S5: pending invitations and requests received (red badge on the Settings tab).
const pendingInvitations = computed(() => badgeCount(store.state.auth?.user))
const { startBannersWatcher, show: showBanner, hide: hideBanner } = useBanners()
const { t, locale } = useI18n()
const { metas } = useMetas()
const toast = useToast()

const normalizeToastSeverity = (message) => message?.severity || 'info'
const toastCardClass = (message) => `app-toast__card app-toast__card--${normalizeToastSeverity(message)}`
const toastAriaRole = (message) => (normalizeToastSeverity(message) === 'error' ? 'alert' : 'status')
const toastAriaLive = (message) => (normalizeToastSeverity(message) === 'error' ? 'assertive' : 'polite')
const toastProgressStyle = (message) => ({
    '--toast-progress-duration': `${Math.max(500, Number(message?.life) || 0)}ms`,
})
const onToastClosed = (event) => {
    if (typeof toast.onMessageClosed === 'function') {
        toast.onMessageClosed(event?.message)
    }
}

const headerRef = ref(null)
const tabsRef = ref(null)
const tabsBarRef = ref(null)
const mainContentRef = ref(null)
// Heights
const headerHeight = ref(0)
const footerHeight = ref(0)

const isLoading = ref(false)

const keyboardVisible = ref(false)
const keyboardHeight = ref(0)

const startX = ref(null)
const startY = ref(null)
const isSwiping = ref(false)
const translateX = ref(0)
const swipeDirection = ref(null)
const pixelsSwipeThreshold = 100

const handleTouchStart = (event) => {
    startX.value = event.touches[0].clientX
    startY.value = event.touches[0].clientY
    isSwiping.value = true
}

const handleTouchMove = (event) => {
    if (startX.value === null || startY.value === null) return

    const currentX = event.touches[0].clientX
    const currentY = event.touches[0].clientY
    const deltaX = currentX - startX.value
    const deltaY = currentY - startY.value

    const screenWidth = window.innerWidth
    const swipeEdgeThreshold = screenWidth * 0.1

    // Seuil pour différencier un balayage horizontal d'un défilement vertical
    const swipeThreshold = 30

    if (!swipeDirection.value) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (Math.abs(deltaX) > swipeThreshold) {
                if (startX.value < swipeEdgeThreshold) {
                    swipeDirection.value = 'right' // Balayage de gauche à droite
                } else if (screenWidth - startX.value < swipeEdgeThreshold) {
                    swipeDirection.value = 'left' // Balayage de droite à gauche
                } else {
                    swipeDirection.value = null
                }
            }
        } else {
            swipeDirection.value = 'scroll'
        }
    }

    // Si la direction du balayage est détectée
    if (swipeDirection.value && swipeDirection.value !== 'scroll') {
        event.preventDefault() // Empêcher le défilement vertical pendant le balayage horizontal
        if (Math.abs(deltaX) > pixelsSwipeThreshold) {
            translateX.value = deltaX > 0 ? pixelsSwipeThreshold + 1 : -pixelsSwipeThreshold - 1
        } else {
            translateX.value = deltaX
        }
    }
}

const handleTouchEnd = (event) => {
    if (!startX.value || !swipeDirection.value) return

    // Signifie que le début était dans la bordure
    if (translateX.value > pixelsSwipeThreshold && swipeDirection.value === 'right') {
        router.back()
    } else if (translateX.value < -pixelsSwipeThreshold && swipeDirection.value === 'left') {
        router.forward()
    }

    startX.value = null
    startY.value = null
    isSwiping.value = false
    swipeDirection.value = null
    translateX.value = 0
}

const movepro = inject('movepro')
const isMobile = inject('isMobile')
const isBuildForMobile = inject('isBuildForMobile')
const appUpdateBannerId = 'appUpdate'
const moveproBlockingIssueBannerId = 'moveproBlockingSensorIssue'

const syncMoveProBlockingIssueBanner = () => {
    if (
        movepro.current.isConnected &&
        movepro.current.hasBlockingSensorIssue &&
        !movepro.current.blockingSensorIssueDismissed
    ) {
        showBanner({
            id: moveproBlockingIssueBannerId,
            name: moveproBlockingIssueBannerId,
            level: 'warning',
            text: t('banner.movepro-blocking-issue-text'),
            shortText: t('banner.movepro-blocking-issue-shortText'),
            onClose: () => {
                movepro.current.dismissBlockingSensorIssue?.()
            },
        })
        return
    }

    hideBanner(moveproBlockingIssueBannerId)
}

Network.addListener('networkStatusChange', (status) => {
    // console.log('Network status changed', status)
    const bannerId = 'youAreOffline'
    if (!status?.connected) {
        showBanner({
            id: bannerId,
            name: bannerId,
            level: 'danger',
            text: t('banner.you-are-offline'),
            shortText: t('banner.you-are-offline'),
        })
    } else {
        hideBanner(bannerId)
    }
})

watch(
    [
        () => movepro.current.isConnected,
        () => movepro.current.hasBlockingSensorIssue,
        () => movepro.current.blockingSensorIssueDismissed,
    ],
    () => {
        syncMoveProBlockingIssueBanner()
    },
    { immediate: true }
)

if (Capacitor.isPluginAvailable('Keyboard')) {
    Keyboard.addListener('keyboardWillShow', (info) => {
        keyboardVisible.value = true
        keyboardHeight.value = info.keyboardHeight || 300
        document.documentElement.classList.add('keyboard-visible')
    })

    Keyboard.addListener('keyboardDidShow', () => {
        const activeElement = document.activeElement
        const inputs = ['input', 'textarea']

        if (activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1) {
            activeElement.scrollIntoView({
                block: 'center',
                inline: 'nearest',
                behavior: 'smooth',
            })
        }
    })

    Keyboard.addListener('keyboardWillHide', () => {
        // console.log('keyboard will hide')
        keyboardVisible.value = false
        keyboardHeight.value = 0
        document.documentElement.classList.remove('keyboard-visible')
    })

    Keyboard.addListener('keyboardDidHide', () => {
        // console.log('keyboard did hide')
        document.documentElement.classList.remove('keyboard-visible')
    })

    if (Capacitor.getPlatform() === 'ios' && isMobile) Keyboard.setAccessoryBarVisible({ isVisible: true })
}

const currentRouteLayout = computed(() => router.currentRoute.value?.meta?.layout)
const TAB_LENS_HORIZONTAL_INSET = 5
// Below this much room on each side of its label, the pill stops hugging its tab...
const TAB_LENS_LABEL_MIN_MARGIN = 4
// ...and wraps the label with this much room on each side instead.
const TAB_LENS_LABEL_PADDING = 8
const TAB_LENS_DRAG_THRESHOLD = 5
const TAB_LENS_MAX_STRETCH = 22

const isTabLensDragging = ref(false)
const isTabLensPressed = ref(false)
const isTabLensReady = ref(false)
const tabLensX = ref(0)
const tabLensWidth = ref(0)
const tabLensPreviewIndex = ref(-1)
const tabLensSettlingIndex = ref(-1)

let tabsPointerInteraction = null
let suppressTabsClick = false
let tabsClickResetTimer
let tabsNavigationSequence = 0

const mobileActiveTabIndex = computed(() => {
    switch (currentRouteLayout.value) {
        case 'sessions':
            return 0
        case 'horses':
            return 1
        case 'record':
            return 2
        case 'stablehealth':
        case 'performance':
        case 'coaching':
        case 'protocols':
            return 3
        case 'settings':
            return 4
        default:
            return -1
    }
})

const mobileVisualActiveTabIndex = computed(() =>
    isTabLensDragging.value && tabLensPreviewIndex.value >= 0
        ? tabLensPreviewIndex.value
        : tabLensSettlingIndex.value >= 0
          ? tabLensSettlingIndex.value
          : mobileActiveTabIndex.value
)

const mobileTabTargets = computed(() => [
    { name: 'sessions.index' },
    { name: 'horses.index' },
    {
        name:
            movepro.current.isRecording || movepro.current.isPaused
                ? 'record.live'
                : store.state.phoneRecording?.isRecording
                  ? 'record.simple'
                  : 'record.index',
    },
    { name: 'stablehealth.index' },
    { name: 'settings.index' },
])

const tabLensStyle = computed(() => ({
    '--mobile-liquid-tabs-lens-x': `${tabLensX.value}px`,
    '--mobile-liquid-tabs-lens-width': `${tabLensWidth.value}px`,
}))

// The label box is as wide as its tab (width: 100%), so the text is measured
// through a range to get the width of the words themselves. A range reports the
// whole text even when an ellipsis cuts it, hence the cap at the box.
const textWidth = (element) => {
    const range = document.createRange()
    range.selectNodeContents(element)
    return Math.min(range.getBoundingClientRect().width, element.getBoundingClientRect().width)
}

// The pill hugs its tab, except when the label would come too close to its
// edges — the long "Enregistrement" in the middle tab — where it wraps the label.
const lensWidthFor = (metric) => {
    const hugWidth = Math.max(0, metric.width - TAB_LENS_HORIZONTAL_INSET * 2)
    const tooTight = metric.labelWidth > hugWidth - TAB_LENS_LABEL_MIN_MARGIN * 2

    return tooTight ? metric.labelWidth + TAB_LENS_LABEL_PADDING * 2 : hugWidth
}

// Keeps the pill inside the bar, clear of its rounded ends. The pill is placed
// in the bar's padding box, so the bounds are its edges, not the outer tabs'.
const clampLensX = (x, width) => {
    const barWidth = tabsBarRef.value?.clientWidth ?? 0

    return Math.max(TAB_LENS_HORIZONTAL_INSET, Math.min(x, barWidth - TAB_LENS_HORIZONTAL_INSET - width))
}

const getTabMetrics = () => {
    if (!tabsBarRef.value) return []

    return Array.from(tabsBarRef.value.querySelectorAll('.mobile-liquid-tabs__link')).map((link) => {
        const index = Number(link.dataset.tabIndex)
        const left = link.offsetLeft
        const width = link.offsetWidth
        const label = link.querySelector('.mobile-liquid-tabs__label')

        return {
            index,
            left,
            width,
            right: left + width,
            center: left + width / 2,
            labelWidth: label ? textWidth(label) : 0,
        }
    })
}

const positionTabLensAtIndex = (index, { reveal = true } = {}) => {
    const metric = getTabMetrics().find((item) => item.index === index)
    if (!metric) return false

    const width = lensWidthFor(metric)
    tabLensWidth.value = width
    tabLensX.value = clampLensX(metric.center - width / 2, width)

    if (reveal && !isTabLensReady.value) {
        requestAnimationFrame(() => {
            isTabLensReady.value = true
        })
    }

    return true
}

// The pill is sized from the tabs and their labels, which change size after the first layout:
// the labels first render in a fallback font until Inter has loaded, or the language changes.
const tabColumnsObserver = new ResizeObserver(() => {
    if (!isTabLensDragging.value) positionTabLensAtIndex(mobileVisualActiveTabIndex.value, { reveal: false })
})
watch(tabsBarRef, (bar) => {
    tabColumnsObserver.disconnect()
    bar?.querySelectorAll('.mobile-liquid-tabs__link').forEach((link) => tabColumnsObserver.observe(link))
})

const syncTabLensToRoute = async ({ reveal = true } = {}) => {
    await nextTick()
    if (isTabLensDragging.value) return

    if (mobileActiveTabIndex.value < 0) {
        isTabLensReady.value = false
        return
    }

    positionTabLensAtIndex(mobileActiveTabIndex.value, { reveal })
}

const updateTabLensFromPointer = (clientX) => {
    const bar = tabsBarRef.value
    const metrics = getTabMetrics()
    if (!bar || metrics.length === 0) return

    const barRect = bar.getBoundingClientRect()
    const minCenter = metrics[0].center
    const maxCenter = metrics[metrics.length - 1].center
    const localX = Math.max(minCenter, Math.min(clientX - barRect.left, maxCenter))
    const nearestMetric = metrics.reduce((nearest, metric) =>
        Math.abs(metric.center - localX) < Math.abs(nearest.center - localX) ? metric : nearest
    )
    const adjacentMetric = metrics.reduce((nearest, metric) => {
        if (metric.index === nearestMetric.index) return nearest
        if (!nearest) return metric
        return Math.abs(metric.center - nearestMetric.center) < Math.abs(nearest.center - nearestMetric.center)
            ? metric
            : nearest
    }, null)
    const tabSpacing = adjacentMetric ? Math.abs(adjacentMetric.center - nearestMetric.center) : nearestMetric.width
    const distanceFromCenter = Math.abs(localX - nearestMetric.center)
    const stretchProgress = Math.min(1, distanceFromCenter / Math.max(1, tabSpacing / 2))
    const stretch = Math.sin((stretchProgress * Math.PI) / 2) * TAB_LENS_MAX_STRETCH
    const width = lensWidthFor(nearestMetric) + stretch

    tabLensPreviewIndex.value = nearestMetric.index
    tabLensWidth.value = width
    tabLensX.value = clampLensX(localX - width / 2, width)
}

const getPointerTabIndex = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.mobile-liquid-tabs__link') : null
    if (!link || !tabsBarRef.value?.contains(link)) return -1
    return Number(link.dataset.tabIndex)
}

const handleTabsPointerDown = (event) => {
    if (!event.isPrimary || (event.button !== undefined && event.button !== 0)) return

    const tabIndex = getPointerTabIndex(event)
    if (tabIndex < 0) return

    tabsPointerInteraction = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        tabIndex,
        dragged: false,
    }
    isTabLensPressed.value = tabIndex === mobileVisualActiveTabIndex.value
    tabsBarRef.value?.setPointerCapture?.(event.pointerId)
}

const handleTabsPointerMove = (event) => {
    if (!tabsPointerInteraction || tabsPointerInteraction.pointerId !== event.pointerId) return

    const distance = Math.abs(event.clientX - tabsPointerInteraction.startClientX)
    if (!tabsPointerInteraction.dragged && distance < TAB_LENS_DRAG_THRESHOLD) return

    if (!tabsPointerInteraction.dragged) {
        tabsPointerInteraction.dragged = true
        tabLensPreviewIndex.value = tabsPointerInteraction.tabIndex
        isTabLensDragging.value = true
        isTabLensPressed.value = false
    }

    event.preventDefault()
    updateTabLensFromPointer(event.clientX)
}

const releaseTabsPointer = (pointerId) => {
    if (tabsBarRef.value?.hasPointerCapture?.(pointerId)) {
        tabsBarRef.value.releasePointerCapture(pointerId)
    }
}

const handleTabsPointerUp = (event) => {
    if (!tabsPointerInteraction || tabsPointerInteraction.pointerId !== event.pointerId) return

    const interaction = tabsPointerInteraction
    tabsPointerInteraction = null
    isTabLensPressed.value = false
    releaseTabsPointer(event.pointerId)

    if (!interaction.dragged) return

    event.preventDefault()
    suppressTabsClick = true
    clearTimeout(tabsClickResetTimer)
    tabsClickResetTimer = setTimeout(() => {
        suppressTabsClick = false
    }, 0)

    const targetIndex = tabLensPreviewIndex.value
    isTabLensDragging.value = false
    tabLensPreviewIndex.value = -1
    tabLensSettlingIndex.value = targetIndex
    positionTabLensAtIndex(targetIndex)

    const target = mobileTabTargets.value[targetIndex]
    if (!target) {
        tabLensSettlingIndex.value = -1
        syncTabLensToRoute()
        return
    }

    const navigationSequence = ++tabsNavigationSequence
    router
        .push(target)
        .catch((error) => console.error('Mobile tab navigation failed:', error))
        .finally(() => {
            if (navigationSequence !== tabsNavigationSequence) return
            tabLensSettlingIndex.value = -1
            syncTabLensToRoute()
        })
}

const handleTabsPointerCancel = (event) => {
    if (!tabsPointerInteraction || tabsPointerInteraction.pointerId !== event.pointerId) return

    tabsPointerInteraction = null
    releaseTabsPointer(event.pointerId)
    isTabLensDragging.value = false
    isTabLensPressed.value = false
    tabLensPreviewIndex.value = -1
    positionTabLensAtIndex(mobileVisualActiveTabIndex.value)
}

const handleTabsClickCapture = (event) => {
    if (!suppressTabsClick) return

    event.preventDefault()
    event.stopPropagation()
}

const isLoggedIn = computed(() => store.getters['auth/loggedIn'])
const isAppReady = computed(() => store.state.auth.initialCheckDone)

const {
    shouldShowBanner: shouldShowAppUpdateBanner,
    availableVersion: appUpdateAvailableVersion,
    updateStoreUrl: appUpdateStoreUrl,
    openUpdateStore,
} = useAppUpdate({
    metas,
    isReady: isAppReady,
})

// Determines if the tabs should be shown or not based
// on the route `tabs` parameter.
const hasTabs = computed(() => route.meta.tabs ?? true)

// Provide the application with configuration values:
provide('layout-configuration', { headerHeight, footerHeight, keyboardHeight, keyboardVisible })
provide('isLoading', isLoading)

const showToastDesignPreview = () => {
    const previews = [
        { severity: 'success', summary: '[TEST] Success' },
        { severity: 'info', summary: '[TEST] Info' },
        { severity: 'warn', summary: '[TEST] Warn' },
        { severity: 'error', summary: '[TEST] Error' },
        { severity: 'secondary', summary: '[TEST] Secondary' },
        { severity: 'contrast', summary: '[TEST] Contrast' },
    ]

    previews.forEach((message) => {
        toast.add({
            ...message,
            detail: 'Preview de couleur',
            life: 12000,
            bypassQueue: true,
            closable: false,
        })
    })
}

const exchangeImpersonationCode = async (code) => {
    if (!code || Array.isArray(code)) return false

    try {
        const response = await axios.post('/impersonate/exchange', { code })
        await store.dispatch('auth/saveUser', { ...response.data, impersonate: true })
        await loadTranslations(response.data?.user?.locale)
        store.commit('auth/setInitialCheck', true)

        const isGuestRoute = router.currentRoute.value?.meta?.guest
        if (isGuestRoute && store.getters['auth/loggedIn']) {
            await router.push({ name: 'index' })
        }

        return true
    } catch (error) {
        toast.add({
            severity: 'warn',
            summary: error?.message ?? 'Impersonation failed',
            life: 3000,
        })
        return false
    }
}

const buildCurrentRouteLocation = (query) => ({
    path: route.path,
    query,
    hash: route.hash || undefined,
})

const consumeQueryMessages = async () => {
    const query = { ...route.query }
    let hasChanges = false

    Object.keys(CALLBACK_QUERY_FEEDBACK).forEach((key) => {
        const queryValue = normalizeQueryValue(query[key])
        const feedback = CALLBACK_QUERY_FEEDBACK[key]?.[queryValue]
        if (!queryValue || !feedback) return

        toast.add({
            severity: feedback.severity,
            summary: t(feedback.translationKey),
            life: 3000,
        })

        delete query[key]
        hasChanges = true
    })

    const toastPreviewParam = normalizeQueryValue(query['toast-preview'] ?? query.toastPreview)
    if (toastPreviewParam === '1') {
        showToastDesignPreview()
        delete query['toast-preview']
        delete query.toastPreview
        hasChanges = true
    }

    if (!hasChanges) return false

    await router.replace(buildCurrentRouteLocation(query))
    return true
}

const routeEffectsState = {
    running: false,
    queued: false,
}

const MAX_SYNC_LOOP_ITERATIONS = 10
const MAX_SYNC_REQUEUE_DEPTH = 5

const syncRouteSideEffects = async (depth = 0) => {
    if (!store.state.auth.initialCheckDone) return

    if (routeEffectsState.running) {
        routeEffectsState.queued = true
        return
    }

    routeEffectsState.running = true

    try {
        let iterations = 0

        while (store.state.auth.initialCheckDone && iterations < MAX_SYNC_LOOP_ITERATIONS) {
            iterations++

            const activeCallback = getCallbackName(route.query)
            const redirectTarget = readRedirectTarget(route.query.redirect)

            if (activeCallback && callbackRequiresAuth(activeCallback) && route.meta?.guest && redirectTarget) {
                const consumedMessage = await consumeQueryMessages()
                if (consumedMessage) {
                    continue
                }

                break
            }

            const callbackResult = await processCallbackQuery({
                client: axios,
                queryParams: { ...route.query },
                isLoggedIn: isLoggedIn.value,
            })

            if (callbackResult.handled) {
                await router.replace(buildCurrentRouteLocation(callbackResult.query))
                continue
            }

            const consumedMessage = await consumeQueryMessages()
            if (consumedMessage) {
                continue
            }

            break
        }

        if (iterations >= MAX_SYNC_LOOP_ITERATIONS) {
            console.warn('[syncRouteSideEffects] Max loop iterations reached, breaking out')
        }
    } finally {
        routeEffectsState.running = false

        if (routeEffectsState.queued && depth < MAX_SYNC_REQUEUE_DEPTH) {
            routeEffectsState.queued = false
            await syncRouteSideEffects(depth + 1)
        } else if (routeEffectsState.queued) {
            routeEffectsState.queued = false
            console.warn('[syncRouteSideEffects] Max requeue depth reached, dropping queued run')
        }
    }
}

// Dynamic style for main content
const MOBILE_BOTTOM_GUTTER = 16 // px spacing when tabs are visible on mobile

// A tap on the tab you are already on scrolls back to the top. Vue Router
// aborts navigation to the current route; on Sessions the URL carries
// ?period&date, so the tap only changes the query, which keeps the scroll.
const onTabBarTap = (event) => {
    const link = event.target.closest?.('a[href]')

    if (link && link.getAttribute('href') === route.path) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}

const mainContentStyle = computed(() => {
    const chrome = isMobile && hasTabs.value ? footerHeight.value + MOBILE_BOTTOM_GUTTER : footerHeight.value
    // The open keyboard covers the tab bar, so only the keyboard needs room then.
    const bottom = keyboardHeight.value > 0 ? keyboardHeight.value + MOBILE_BOTTOM_GUTTER : chrome
    // Each of the three desktop Navbar columns scrolls on its own, which needs a bounded height.
    const desktopColumns = !isMobile && hasTabs.value

    return {
        paddingTop: `${headerHeight.value}px`,
        paddingBottom: `${bottom}px`,
        // Until the first auth check is done, #topWindow centres the loader on the screen.
        minHeight: store.state.auth.initialCheckDone ? 'var(--app-viewport-height)' : undefined,
        height: desktopColumns ? 'var(--app-viewport-height)' : undefined,
        transition: 'padding 0.2s ease, min-height 0.2s ease',
    }
})

// ResizeObserver logic
let resizeObserver
const updateOffsets = () => {
    headerHeight.value = headerRef.value ? headerRef.value.offsetHeight : 0
    footerHeight.value = tabsRef.value ? tabsRef.value.offsetHeight : 0
}
const MIN_HEADER_PADDING_TOP = isBuildForMobile ? 30 : 0 // en pixels

const ensureMinimalHeaderPadding = () => {
    if (!headerRef.value) return

    const currentPaddingTop = parseFloat(window.getComputedStyle(headerRef.value).paddingTop) || 0

    if (currentPaddingTop < MIN_HEADER_PADDING_TOP) {
        headerRef.value.style.paddingTop = `${MIN_HEADER_PADDING_TOP}px`
    }
}
const calculateWindowSize = () => {
    updateOffsets()
    ensureMinimalHeaderPadding()
    if (!isTabLensDragging.value) {
        positionTabLensAtIndex(mobileActiveTabIndex.value, { reveal: false })
    }
}

watch([tabsBarRef, mobileActiveTabIndex], () => syncTabLensToRoute(), {
    immediate: true,
    flush: 'post',
})

onMounted(async () => {
    const previewCode = normalizeQueryValue(route.query.preview)
    let skipInitialCheck = false
    if (previewCode) {
        const query = { ...route.query }
        delete query.preview
        await router.replace({ query })

        const exchanged = await exchangeImpersonationCode(previewCode)
        if (exchanged) {
            skipInitialCheck = true
        }
    }

    // Initiate the authentication logic.
    if (!skipInitialCheck) {
        await store.dispatch('auth/initialCheck')
    }

    resizeObserver = new ResizeObserver(() => {
        calculateWindowSize()
    })

    // Observe whichever header and tab bar are mounted now: the tab bar is rebuilt after every route
    // without tabs (login, pricing, live recording). border-box, because the heights come from
    // offsetHeight and the tab bar's own padding shrinks when keyboard-visible zeroes the safe area.
    watch(
        [headerRef, tabsRef],
        (elements, previous = []) => {
            previous.forEach((element) => element && resizeObserver.unobserve(element))
            elements.forEach((element) => element && resizeObserver.observe(element, { box: 'border-box' }))
            calculateWindowSize()
        },
        { immediate: true }
    )
    await syncRouteSideEffects()

    watch(
        locale,
        () => {
            const bluetoothStrings = {
                scanning: t('sensor.scanning'), //'Scanning...'
                cancel: t('sensor.cancel'), //'Cancel'
                availableDevices: t('sensor.availableDevices'), //'Available devices'
                noDeviceFound: t('sensor.noDeviceFound'), //'No device found'
            }
            movepro.bluetooth.setDisplayStrings(bluetoothStrings)
        },
        { immediate: true }
    )
})

onUnmounted(() => {
    clearTimeout(tabsClickResetTimer)
    tabColumnsObserver.disconnect()
    if (resizeObserver) {
        resizeObserver.disconnect()
    }
})

watch([() => route.fullPath, () => store.state.auth.initialCheckDone, isLoggedIn], ([, initialCheckDone]) => {
    if (!initialCheckDone) return

    // Skip when on a guest page with an auth-required callback — the login
    // page will navigate to the redirect target after authentication completes,
    // which will then trigger this watcher on the destination route.
    const activeCallback = getCallbackName(route.query)
    if (activeCallback && callbackRequiresAuth(activeCallback) && route.meta?.guest) return

    syncRouteSideEffects().catch((error) => {
        console.error('Error syncing route side effects:', error)
    })
})

// scrollIntoView() keeps fields clear of the fixed tab bar.
watch(
    footerHeight,
    (height) => {
        document.documentElement.style.scrollPaddingBottom = `${height}px`
    },
    { immediate: true }
)

watch(
    [shouldShowAppUpdateBanner, appUpdateAvailableVersion, appUpdateStoreUrl],
    () => {
        if (!shouldShowAppUpdateBanner.value) {
            hideBanner(appUpdateBannerId)
            return
        }

        const bannerPayload = {
            id: appUpdateBannerId,
            name: appUpdateBannerId,
            level: 'info',
            text: appUpdateAvailableVersion.value
                ? t('banner.app-update-text', { version: appUpdateAvailableVersion.value })
                : t('banner.app-update-text-generic'),
            shortText: t('banner.app-update-shortText'),
        }

        if (appUpdateStoreUrl.value) {
            bannerPayload.buttonText = t('banner.app-update-button')
            bannerPayload.buttonLink = openUpdateStore
        }

        hideBanner(appUpdateBannerId)
        showBanner(bannerPayload)
    },
    { immediate: true }
)

watch(
    () => store.getters['auth/loggedIn'],
    async (isLoggedIn) => {
        if (isLoggedIn) {
            // Explicit alert for collect location data (Android)
            if (Capacitor.getPlatform() === 'android' && isMobile) {
                const isAndroidPopupDisplayed = await Memory.get('androidPopupDisplayed')

                if (!isAndroidPopupDisplayed) {
                    alert(t('location.androidalert'))
                    Memory.set('androidPopupDisplayed', true)
                }
            }
            //delay the execution of the watcher to be sure the user locale is correctly loaded
            setTimeout(() => {
                startBannersWatcher()
                setTimeout(() => {
                    if (
                        store.state.auth?.user?.subscription?.active &&
                        isTemporarySubscription(store.state.auth?.user) &&
                        route?.params?.sessionId !== metas.value?.['session-demo-id']
                    ) {
                        const bannerId = 'freePremiumAccess'
                        showBanner({
                            id: bannerId,
                            name: bannerId,
                            level: 'info',
                            text: t('banner.free-premium-access-text'),
                            shortText: t('banner.free-premium-access-shortText'),
                            buttonText: t('banner.free-premium-access-button'),
                            buttonLink: () => {
                                router.push({
                                    name: 'sessions.session',
                                    params: { sessionId: metas.value?.['session-demo-id'] },
                                })
                                hideBanner(bannerId)
                            },
                        })
                    }
                }, 1000)
            }, 3000)
        }

        if (
            Capacitor.getPlatform() !== 'android' &&
            Capacitor.getPlatform() !== 'ios' &&
            !movepro.bluetooth.hasWebAPIAvailble
        ) {
            console.log('Bluetooth API not available in this browser.')
        } else {
            movepro.bluetooth.askPermissions({ atStartup: true })
        }
    }
)
</script>
